import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Chip
} from '@mui/material';
import ReactPlayer from 'react-player';
import { motion } from 'framer-motion';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import LockIcon from '@mui/icons-material/Lock';

// Sign-specific video IDs for each month
const signVideoIds = {
  january: {
    aries: 'psHx0WK512E',
    taurus: 'pdVXRjWYp9k',
    gemini: 'qMnEBR07h9w',
    cancer: '7d-XFVwRR8E',
    leo: 'cWnYVpRmcuI',
    virgo: 'YOUR_VIRGO_VIDEO_ID',
    libra: 'ld_2wVTIJ1k',
    scorpio: 'CxswBw6Pkkg',
    sagittarius: 'vhl6tp2LZEo',
    capricorn: 'EUdeJp1Dg0g',
    aquarius: 'I_xfMuu2Cac',
    pisces: 'cSgozM3K8Ec'
  },
  february: {
    aries: '59zS-JgUxj0',
    taurus: 'YSBG9DeALJ0',
    gemini: '-sIQFhTMGRk',
    cancer: 'dyi1iSh3Ihg',
    leo: 'ZoyUPo31nZE',
    virgo: 'sAPoOcDXSiM',
    libra: 'tWxMjf51Xek',
    scorpio: 'elw8vz6b7iI',
    sagittarius: 'TU9zhEEdEZo',
    capricorn: 'WlKCgyetCtM',
    aquarius: 'esWYUJo3uHk',
    pisces: 'S8Js5REx_b8'
  },
  march: {
    aries: 'SHcU6wPZP5k',
    taurus: 'SHcU6wPZP5k',
    gemini: 'SHcU6wPZP5k',
    cancer: 'SHcU6wPZP5k',
    leo: 'SHcU6wPZP5k',
    virgo: 'SHcU6wPZP5k',
    libra: 'SHcU6wPZP5k',
    scorpio: 'SHcU6wPZP5k',
    sagittarius: 'SHcU6wPZP5k',
    capricorn: 'SHcU6wPZP5k',
    aquarius: 'SHcU6wPZP5k',
    pisces: 'SHcU6wPZP5k'
  },
  april: {
    aries: 'uz5Y7sodI_g',
    taurus: 'Vfhh0ELwzK8',
    gemini: 'QxDD3G_fIYA',
    cancer: '_VJsegS7Htg',
    leo: '5xiXT9YCJtQ',
    virgo: '7IWEeKuLTBs',
    libra: 'D3uqM0NNXhI',
    scorpio: 'Dmhb4HMqsK0',
    sagittarius: '-mrSowDadsk',
    capricorn: '5KvoWAbV3-g',
    aquarius: 'kokVHnSgPoQ',
    pisces: '6lwAEArD1Do'
  },
  may: {},
  june: {},
  july: {},
  august: {},
  september: {},
  october: {},
  november: {},
  december: {}
};

const signColors = {
  aries: '#FF6B6B',
  taurus: '#4ECDC4',
  gemini: '#FFE66D',
  cancer: '#A8E6CF',
  leo: '#FFD93D',
  virgo: '#95E1D3',
  libra: '#FFA8B5',
  scorpio: '#B983FF',
  sagittarius: '#FF8C42',
  capricorn: '#6C5B7B',
  aquarius: '#4ECDC4',
  pisces: '#C8A2C8'
};

const monthNames = {
  january: 'January',
  february: 'February',
  march: 'March',
  april: 'April',
  may: 'May',
  june: 'June',
  july: 'July',
  august: 'August',
  september: 'September',
  october: 'October',
  november: 'November',
  december: 'December'
};

const MonthlyDetail = () => {
  const { sign, month } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';

  const color = signColors[sign?.toLowerCase()] || '#667eea';
  const monthName = monthNames[month?.toLowerCase()] || month;
  const videoId = signVideoIds[month?.toLowerCase()]?.[sign?.toLowerCase()];

  return (
    <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
          py: 6,
          borderBottom: `4px solid ${color}`
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textTransform: 'capitalize' }}>
            {sign} Rising - {monthName} 2026 Forecast
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Monthly planetary movements and their impact on your life
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate(`/rising/${sign}/monthly${isAdminMode ? '?admin=true' : ''}`)}
            sx={{ mt: 3, borderColor: color, color: color }}
          >
            ← Back to Monthly Forecasts
          </Button>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, md: 3 } }}>
        {/* Free Video */}
        {videoId && videoId !== 'YOUR_VIRGO_VIDEO_ID' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PlayCircleIcon sx={{ color: '#4CAF50', mr: 1, fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Watch Your Free {monthName} 2026 Overview
                </Typography>
              </Box>

              <Paper sx={{ p: { xs: 1, md: 2 }, bgcolor: 'black' }}>
                <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
                  <ReactPlayer
                    url={`https://youtube.com/watch?v=${videoId}`}
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    controls={true}
                    config={{
                      youtube: {
                        playerVars: {
                          showinfo: 1,
                          modestbranding: 1,
                          rel: 0
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Paper>
          </motion.div>
        )}

        {/* Extended 2026 Cosmic Overview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Paper sx={{ p: { xs: 3, md: 4 }, bgcolor: `${color}11` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ color: color, mr: 1, fontSize: 32 }} />
                <Typography variant="h4" sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                }}>
                  Extended 2026 Cosmic Overview
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h3" sx={{
                  color: color,
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
                }}>
                  $29.99
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-time payment
                </Typography>
              </Box>
            </Box>

            <Typography color="text.secondary" paragraph sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }}>
              Get the complete 2026 forecast for {sign?.charAt(0).toUpperCase() + sign?.slice(1)} Rising with:
            </Typography>

            <Box component="ul" sx={{ mb: 3, pl: 2, '& li': { mb: 1, fontSize: { xs: '0.95rem', md: '1.05rem' } } }}>
              <li>90+ Minute Video Deep Dive</li>
              <li>House by House Transits & Degrees impacted</li>
              <li>Month by month timing guidance for major transits</li>
              <li>Comprehensive PDF Report (request the written format FREE)</li>
              <li>Birth Chart Wheel with visual aspects</li>
              <li>Key Activation Windows</li>
              <li>House activations</li>
              <li>Lifetime access</li>
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{
                py: { xs: 2, md: 2.5 },
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem' },
                bgcolor: color,
                '&:hover': { bgcolor: color, opacity: 0.9 }
              }}
              onClick={() => navigate('/pricing?section=forecasts')}
            >
              View Pricing Options
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                30-Day Money-Back Guarantee
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MonthlyDetail;
